import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { X, Edit, Mail, Phone, Copy, Check } from "react-feather";
import {
  getCustomerProfile,
  getCustomerBookings,
} from "../../../../actions/admin/crm-actions";

/* ─── Avatar color palette (matches reference site) ─────────────── */
const AVATAR_PALETTE = [
  { bg: "#e2f0ff", color: "#2868a0" },   // blue
  { bg: "#eee7ff", color: "#6b4db1" },   // violet
  { bg: "#ffeed8", color: "#c66b04" },   // orange
  { bg: "#e2f6ea", color: "#238854" },   // green
  { bg: "#ffe6ef", color: "#aa4167" },   // pink
  { bg: "#dff6f5", color: "#187d79" },   // teal
  { bg: "#e2edf5", color: "#295b78" },   // navy
  { bg: "#fff3cd", color: "#9b7411" },   // gold
];

const getAvatarColor = (firstName = "", lastName = "") => {
  const str = (firstName + lastName).toLowerCase();
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

/* ─── helpers ──────────────────────────────────────────────────── */
const getInitials = (firstName = "", lastName = "") =>
  `${(firstName[0] || "").toUpperCase()}${(lastName[0] || "").toUpperCase()}`;

const formatCurrency = (v) =>
  v !== null && v !== undefined
    ? `$${parseFloat(v).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
    : "—";

const getStatusLabel = (code) => {
  const map = {
    A: "Upcoming", P: "Pending", C: "Confirmed", R: "Rejected",
    CC: "Cancelled", CS: "Cancelled", CO: "Completed",
    MN: "No-Show", RITR: "Rescheduling",
  };
  return map[code] || code || "—";
};

const getStatusBadgeClass = (code, isUpcoming) => {
  if (isUpcoming) return "crm-bk-badge crm-bk-badge--upcoming";
  const map = {
    CO: "crm-bk-badge crm-bk-badge--completed",
    CC: "crm-bk-badge crm-bk-badge--cancelled",
    CS: "crm-bk-badge crm-bk-badge--cancelled",
    MN: "crm-bk-badge crm-bk-badge--noshow",
    P:  "crm-bk-badge crm-bk-badge--pending",
    C:  "crm-bk-badge crm-bk-badge--pending",
  };
  return map[code] || "crm-bk-badge";
};

/* ══════════════════════════════════════════════════════════════════
   CustomerProfile — single-scroll panel
   ══════════════════════════════════════════════════════════════════ */
const CustomerProfile = (props) => {
  const { customer, onClose, onEdit } = props;

  const [profileData, setProfileData] = useState(null);
  const [bookings, setBookings]       = useState([]);
  const [isLoadingBk, setIsLoadingBk] = useState(false);
  const [isLoadingPr, setIsLoadingPr] = useState(false);
  const [copied, setCopied]           = useState(false);

  /* ── load data ── */
  const loadProfile = useCallback(() => {
    if (!customer?.id) return;
    setIsLoadingPr(true);
    props.getCustomerProfile({
      customerId: customer.id,
      onSuccess: (data) => { setProfileData(data); setIsLoadingPr(false); },
      onError:   ()     => setIsLoadingPr(false),
    });
  }, [customer?.id]);

  const loadBookings = useCallback(() => {
    if (!customer?.id) return;
    setIsLoadingBk(true);
    props.getCustomerBookings({
      customerId: customer.id,
      data: { pageSize: 50, pageNumber: 1 },
      onSuccess: (data) => { setBookings(data.entity || []); setIsLoadingBk(false); },
      onError:   ()     => setIsLoadingBk(false),
    });
  }, [customer?.id]);

  useEffect(() => {
    loadProfile();
    loadBookings();
  }, [customer?.id]);

  /* ── copy contact details ── */
  const handleCopyDetails = () => {
    const text = [
      `${customer.first_name || ""} ${customer.last_name || ""}`.trim(),
      customer.email,
      customer.phone,
      customer.address_line_1,
    ].filter(Boolean).join("\n");
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!customer) return null;

  const fullName    = `${customer.first_name || ""} ${customer.last_name || ""}`.trim();
  const initials    = getInitials(customer.first_name, customer.last_name);
  const avatarColor = getAvatarColor(customer.first_name, customer.last_name);
  const address     = [customer.address_line_1, customer.city, customer.state, customer.zip,
    customer.country?.label].filter(Boolean).join(", ");

  return (
    <>
      {/* backdrop */}
      <div className="crm-profile-backdrop" onClick={onClose} />

      {/* panel */}
      <div className="crm-profile-panel">

        {/* ── header ── */}
        <div className="crm-profile-panel__header">
          <div>
            <span className="crm-profile-panel__label">CUSTOMER PROFILE</span>
            <div className="crm-profile-panel__name">{fullName}</div>
          </div>
          <button className="crm-profile-panel__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* ── body ── */}
        <div className="crm-profile-panel__body">

          {/* identity card */}
          <div className="crm-id-card">
            <div className="crm-id-card__avatar"
              style={{ background: avatarColor.bg, color: avatarColor.color }}>
              {initials}
            </div>
            <div className="crm-id-card__info">
              <div className="crm-id-card__fullname">{fullName}</div>
              {customer.email && (
                <div className="crm-id-card__contact-row">
                  <Mail size={13} className="crm-id-card__icon" />
                  <span>{customer.email}</span>
                </div>
              )}
              {customer.phone && (
                <div className="crm-id-card__contact-row">
                  <Phone size={13} className="crm-id-card__icon" />
                  <span>{customer.phone}</span>
                </div>
              )}
            </div>
            {onEdit && (
              <button
                className="crm-id-card__edit-btn"
                onClick={() => onEdit(customer)}
              >
                <Edit size={13} className="mr-1" /> Edit
              </button>
            )}
          </div>

          {/* stats row */}
          <div className="crm-stats-row">
            <div className="crm-stat-card">
              <span className="crm-stat-card__label">Last booking</span>
              <span className="crm-stat-card__value">
                {profileData?.last_booking_date ?? "—"}
              </span>
            </div>
            <div className="crm-stat-card">
              <span className="crm-stat-card__label">Next booking</span>
              <span className={`crm-stat-card__value${profileData?.next_booking_date ? " crm-stat-card__value--hl" : ""}`}>
                {profileData?.next_booking_date ?? "—"}
              </span>
            </div>
            <div className="crm-stat-card">
              <span className="crm-stat-card__label">Total bookings</span>
              <span className="crm-stat-card__value">
                {profileData?.total_bookings ?? "—"}
              </span>
            </div>
          </div>

          {/* contact information */}
          <div className="crm-section-card">
            <div className="crm-section-card__header">
              <span className="crm-section-card__title">Contact information</span>
              <button className="crm-section-card__action" onClick={handleCopyDetails}>
                {copied ? <Check size={13} className="mr-1" /> : <Copy size={13} className="mr-1" />}
                {copied ? "Copied!" : "Copy details"}
              </button>
            </div>
            <div className="crm-contact-grid">
              {customer.email && (
                <div className="crm-contact-item">
                  <span className="crm-contact-item__label">Email</span>
                  <span className="crm-contact-item__value">{customer.email}</span>
                </div>
              )}
              {customer.phone && (
                <div className="crm-contact-item">
                  <span className="crm-contact-item__label">Phone</span>
                  <span className="crm-contact-item__value crm-contact-item__value--phone">
                    {customer.phone}
                  </span>
                </div>
              )}
              {address && (
                <div className="crm-contact-item crm-contact-item--full">
                  <span className="crm-contact-item__label">Address</span>
                  <span className="crm-contact-item__value">{address}</span>
                </div>
              )}
              {!customer.email && !customer.phone && !address && (
                <div className="crm-bk-empty">No contact information available.</div>
              )}
            </div>
          </div>

          {/* booking history */}
          <div className="crm-section-card">
            <div className="crm-section-card__header">
              <span className="crm-section-card__title">Booking history</span>
              {bookings.length > 0 && (
                <span className="crm-section-card__count">{bookings.length} shown</span>
              )}
            </div>
            <p className="crm-section-card__sub">Bookings associated with this customer only</p>
            {isLoadingBk ? (
              <div className="crm-loading">Loading…</div>
            ) : bookings.length === 0 ? (
              <div className="crm-bk-empty">No bookings found for this customer.</div>
            ) : (
              <div className="crm-bk-list">
                {bookings.map((b) => (
                  <div key={b.id} className="crm-bk-item">
                    <div className="crm-bk-item__date-box">
                      <span className="crm-bk-item__day">{b.booking_day}</span>
                      <span className="crm-bk-item__month">{b.booking_month}</span>
                    </div>
                    <div className="crm-bk-item__body">
                      <div className="crm-bk-item__top">
                        <span className="crm-bk-item__name">{b.event_name}</span>
                        <span className={getStatusBadgeClass(b.booking_status_code, b.is_upcoming)}>
                          {b.is_upcoming ? "Upcoming" : getStatusLabel(b.booking_status_code)}
                        </span>
                      </div>
                      <p className="crm-bk-item__datetime">
                        {b.booking_date} · {b.booking_time}
                      </p>
                      <div className="crm-bk-item__meta">
                        {b.staff_name && (
                          <span className="crm-bk-item__staff">👤 {b.staff_name}</span>
                        )}
                        {b.event_format && (
                          <span className="crm-bk-item__venue">🔗 {b.event_format}</span>
                        )}
                        <span className="crm-bk-item__amount">
                          {b.total_amount > 0 ? formatCurrency(b.total_amount) : "Free"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>{/* end body */}

      </div>{/* end panel */}
    </>
  );
};

export default connect(null, {
  getCustomerProfile,
  getCustomerBookings,
})(CustomerProfile);
